import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/pl";
import {
    Box, HStack, VStack, Text, SimpleGrid, Button, Skeleton, SkeletonCircle,
} from "@chakra-ui/react";
import {
    HiOutlineUserGroup, HiOutlineCalendarDays, HiOutlineClock, HiOutlineStar,
    HiOutlinePlus, HiOutlineCheck, HiOutlinePencil, HiOutlineChevronRight,
} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { useAuth } from "@/auth/AuthContext";
import { useAgentTypes } from "@/components/layout/AgentTypesContext";
import { useRecruitmentDashboardData } from "@/components/layout/RecruitmentDashboardDataContext";
import NewRecruitmentModal from "./NewRecruitmentModal";

dayjs.extend(relativeTime);
dayjs.locale("pl");

const RECRUITMENTS_TO_DISPLAY = 4;
const CANDIDATES_TO_DISPLAY = 7;
const ACTIVITY_FEED_LIMIT = 4;

const ACTIVITY_STYLES = {
    status: { bg: "#DCFCE7", color: "#16A34A", icon: <HiOutlineCheck /> },
    note: { bg: "#F3F4F6", color: "#4B5563", icon: <HiOutlinePencil /> },
};

const STATUS_STYLES = {
    initialized: { bg: "#EAEAF2", color: "#65616F" },
    created: { bg: "#E1EEFD", color: "#1D4ED8" },
    finalized: { bg: "#FDEFDC", color: "#B45309" },
    qualifiedfornextstage: { bg: "#F1E8FE", color: "#6D28D9" },
    furtherselection: { bg: "#E1F7FA", color: "#0E7C86" },
    readytohire: { bg: "#E1F5F1", color: "#0F766E" },
    hired: { bg: "#E4F7EC", color: "#178A4C" },
    rejected: { bg: "#FCE8EC", color: "#C81E3A" },
    candidateresigned: { bg: "#EAEAF2", color: "#65616F" },
};

const StatusPill = ({ status, t }) => {
    const key = status?.toLowerCase() ?? "initialized";
    const style = STATUS_STYLES[key] ?? STATUS_STYLES.initialized;
    return (
        <Text as="span" bg={style.bg} color={style.color} fontSize="10.5px" fontWeight={600} px={2.5} py={0.5} borderRadius="999px" whiteSpace="nowrap" flexShrink={0}>
            {t(`candidateStatus.${key}`)}
        </Text>
    );
};

const timeAgo = (date) => dayjs(date).fromNow();

const initialsOf = (name) =>
    (name ?? "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const fullNameOf = (candidate, fallback) =>
    [candidate.firstname, candidate.lastname].filter(Boolean).join(" ") || fallback;

const StatCard = ({ label, value, icon, iconBg, iconColor }) => (
    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
        <HStack gap={3} align="center">
            <Box w="36px" h="36px" borderRadius={10} bg={iconBg} color={iconColor} display="flex" alignItems="center" justifyContent="center" fontSize="18px" flexShrink={0}>
                {icon}
            </Box>
            <VStack align="flex-start" gap={0} minW={0}>
                <Text color="gray.900" fontSize="xl" fontWeight={700} lineHeight={1.1}>{value}</Text>
                <Text color="gray.500" fontSize="xs" truncate>{label}</Text>
            </VStack>
        </HStack>
    </Box>
);

const CandidateRow = ({ candidate, recruitment, onClick, t, tModels }) => (
    <HStack
        w="full"
        justify="space-between"
        gap={3}
        px={4}
        py={2.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={onClick}
    >
        <HStack gap={3} minW={0}>
            <Box w="34px" h="34px" borderRadius="full" bg="gray.100" color="gray.600" display="flex" alignItems="center" justifyContent="center" fontWeight={700} fontSize="xs" flexShrink={0}>
                {initialsOf(fullNameOf(candidate, "??"))}
            </Box>
            <VStack align="flex-start" gap={0} minW={0}>
                <Text color="gray.900" fontSize="sm" fontWeight={600} truncate>
                    {fullNameOf(candidate, tModels("candidatePage.unnamedCandidate"))}
                </Text>
                <Text color="gray.400" fontSize="xs" truncate>
                    {recruitment.name} · {timeAgo(candidate.createdAt)}
                </Text>
            </VStack>
        </HStack>
        <StatusPill status={candidate.status} t={tModels} />
    </HStack>
);

const RecruitmentRow = ({ recruitment, onClick, t }) => (
    <HStack
        w="full"
        justify="space-between"
        gap={2}
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={onClick}
    >
        <Text color="gray.800" fontSize="sm" fontWeight={500} truncate minW={0} flex={1}>
            {recruitment.name}
        </Text>
        <HStack gap={2} flexShrink={0}>
            <Text color="gray.400" fontSize="xs" whiteSpace="nowrap">
                {recruitment.candidateCount ?? "—"} {t("recruitmentDashboard.candidatesSuffix")}
            </Text>
            <HiOutlineChevronRight size={14} color="#9A96A6" />
        </HStack>
    </HStack>
);

const ActivityRow = ({ item, onClick }) => {
    const style = ACTIVITY_STYLES[item.type] ?? ACTIVITY_STYLES.note;
    return (
        <HStack
            align="flex-start"
            gap={2.5}
            w="full"
            cursor="pointer"
            borderRadius={8}
            p={1}
            m={-1}
            _hover={{ bg: "gray.50" }}
            onClick={onClick}
        >
            <Box w="24px" h="24px" borderRadius="full" bg={style.bg} color={style.color} display="flex" alignItems="center" justifyContent="center" fontSize="12px" flexShrink={0}>
                {style.icon}
            </Box>
            <VStack align="flex-start" gap={0} minW={0}>
                <Text fontSize="xs" color="gray.800" lineHeight={1.4}>
                    <Text as="span" fontWeight={700} color="gray.900">{item.actorName}</Text> {item.text}
                </Text>
                <Text fontSize="xs" color="gray.400">{timeAgo(item.at)}</Text>
            </VStack>
        </HStack>
    );
};

const DashboardSkeleton = () => (
    <VStack w="full" py={6} gap={4} align="flex-start">
        <HStack w="full" justify="space-between" align="flex-start">
            <VStack align="flex-start" gap={2}>
                <Skeleton height="28px" width="220px" borderRadius={6} />
                <Skeleton height="16px" width="280px" borderRadius={6} />
            </VStack>
            <Skeleton height="40px" width="160px" borderRadius={8} />
        </HStack>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} w="full">
            {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                    <HStack gap={3}>
                        <Skeleton width="36px" height="36px" borderRadius={10} />
                        <VStack align="flex-start" gap={2} flex={1}>
                            <Skeleton height="18px" width="40%" borderRadius={4} />
                            <Skeleton height="12px" width="70%" borderRadius={4} />
                        </VStack>
                    </HStack>
                </Box>
            ))}
        </SimpleGrid>

        <HStack w="full" align="flex-start" gap={4} flexWrap={{ base: "wrap", lg: "nowrap" }}>
            <Box flex="3" minW={{ base: "full", lg: "0" }} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                <Skeleton height="18px" width="160px" borderRadius={4} mb={4} />
                <VStack align="stretch" gap={4}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <HStack key={i} gap={3}>
                            <SkeletonCircle size="34px" />
                            <VStack align="flex-start" gap={2} flex={1}>
                                <Skeleton height="14px" width="45%" borderRadius={4} />
                                <Skeleton height="10px" width="65%" borderRadius={4} />
                            </VStack>
                        </HStack>
                    ))}
                </VStack>
            </Box>

            <VStack flex="2" minW={{ base: "full", lg: "260px" }} gap={4} align="stretch">
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                    <Skeleton height="16px" width="140px" borderRadius={4} mb={3} />
                    <VStack align="stretch" gap={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="14px" width="100%" borderRadius={4} />
                        ))}
                    </VStack>
                </Box>
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                    <Skeleton height="16px" width="140px" borderRadius={4} mb={3} />
                    <VStack align="stretch" gap={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="14px" width="100%" borderRadius={4} />
                        ))}
                    </VStack>
                </Box>
            </VStack>
        </HStack>
    </VStack>
);

const RecruitmentDashboardPage = () => {
    const { t } = useTranslation("home");
    const { t: tModels } = useTranslation("models");
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { recruiterAgents, isLoading: agentsLoading } = useAgentTypes();
    const { activeRecruitments, activeTotalCount, sampledCandidates, metrics, isLoading: isLoadingData, load } = useRecruitmentDashboardData();

    useSetPageHeader(t("recruitmentDashboard.headerTitle"), t("recruitmentDashboard.headerSubtitle"), <HiOutlineUserGroup />);

    const [isNewRecruitmentModalOpen, setIsNewRecruitmentModalOpen] = useState(false);

    // The context caches the result after the first load, so switching to /sales and back
    // here won't re-fetch -- see RecruitmentDashboardDataContext.
    useEffect(() => {
        load();
    }, [load]);

    // The agent id in this route is decorative (the nested pages fetch purely by
    // recruitment/candidate id), so any of the user's recruiter agents is a valid segment.
    const resolveAgentId = () => recruiterAgents[0]?.id;

    const recentCandidates = useMemo(() =>
        [...sampledCandidates]
            .sort((a, b) => new Date(b.candidate.createdAt) - new Date(a.candidate.createdAt))
            .slice(0, CANDIDATES_TO_DISPLAY),
    [sampledCandidates]);

    const activityFeed = useMemo(() => {
        const notes = sampledCandidates
            .filter(({ candidate }) => candidate.lastNote)
            .map(({ candidate, recruitment }) => ({
                id: `note-${candidate.lastNote.id}`,
                type: "note",
                at: candidate.lastNote.createdAt,
                actorName: fullNameOf(candidate, tModels("candidatePage.unnamedCandidate")),
                text: t("recruitmentDashboard.activityNote"),
                candidateId: candidate.id,
                recruitment,
            }));

        const statusChanges = sampledCandidates
            .filter(({ candidate }) => candidate.lastTimelineEvent?.kind === "StatusChange")
            .map(({ candidate, recruitment }) => ({
                id: `status-${candidate.id}-${candidate.lastTimelineEvent.id}`,
                type: "status",
                at: candidate.lastTimelineEvent.occurredAt,
                actorName: fullNameOf(candidate, tModels("candidatePage.unnamedCandidate")),
                text: t("recruitmentDashboard.activityStatus", {
                    status: tModels(`candidateStatus.${candidate.status?.toLowerCase()}`),
                }),
                candidateId: candidate.id,
                recruitment,
            }));

        return [...notes, ...statusChanges]
            .sort((a, b) => new Date(b.at) - new Date(a.at))
            .slice(0, ACTIVITY_FEED_LIMIT);
    }, [sampledCandidates, t, tModels]);

    if (agentsLoading || isLoadingData) {
        return <DashboardSkeleton />;
    }

    if (recruiterAgents.length === 0) {
        return (
            <VStack w="full" py={12} align="center" gap={4}>
                <Text color="gray.500">{t("recruitmentDashboard.emptyAgents")}</Text>
                <Button variant="primary" onClick={() => navigate("/models")}>
                    {t("recruitmentDashboard.emptyAgentsCta")}
                </Button>
            </VStack>
        );
    }

    const displayedRecruitments = activeRecruitments.slice(0, RECRUITMENTS_TO_DISPLAY);

    return (
        <VStack w="full" pt={2} pb={6} gap={4} align="flex-start">
            <HStack w="full" justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
                <VStack align="flex-start" gap={1}>
                    <Text fontSize="2xl" fontWeight={700} color="gray.900">
                        {t("recruitmentDashboard.greeting", { name: currentUser?.firstName ?? "" })}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                        {t("recruitmentDashboard.greetingSubtitle", { date: dayjs().format("D MMMM") })}
                    </Text>
                </VStack>
                <Button variant="primary" onClick={() => setIsNewRecruitmentModalOpen(true)}>
                    <HiOutlinePlus /> {t("recruitmentDashboard.newRecruitmentCta")}
                </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={3} w="full">
                <StatCard
                    label={t("recruitmentDashboard.activeRecruitmentsMetric")}
                    value={activeTotalCount}
                    icon={<HiOutlineCalendarDays />}
                    iconBg="#EDE9FE"
                    iconColor="#6D28D9"
                />
                <StatCard
                    label={t("recruitmentDashboard.newCandidatesMetric")}
                    value={metrics.newCandidates}
                    icon={<HiOutlineUserGroup />}
                    iconBg="#DCFCE7"
                    iconColor="#15803D"
                />
                <StatCard
                    label={t("recruitmentDashboard.toReviewMetric")}
                    value={metrics.toReview}
                    icon={<HiOutlineClock />}
                    iconBg="#FEF3C7"
                    iconColor="#B45309"
                />
                <StatCard
                    label={t("recruitmentDashboard.favouritesMetric")}
                    value={metrics.favourites}
                    icon={<HiOutlineStar />}
                    iconBg="#DBEAFE"
                    iconColor="#1D4ED8"
                />
            </SimpleGrid>

            <HStack w="full" align="flex-start" gap={4} flexWrap={{ base: "wrap", lg: "nowrap" }}>
                {/* Primary: newest candidates -- the thing recruiters actually check first */}
                <Box flex="3" minW={{ base: "full", lg: "0" }} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                    <HStack justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                        <Text fontWeight={700} color="gray.900" fontSize="sm">{t("recruitmentDashboard.recentCandidatesTitle")}</Text>
                    </HStack>
                    {recentCandidates.length === 0 ? (
                        <Text color="gray.500" fontSize="sm" p={4}>{t("recruitmentDashboard.emptyRecentCandidates")}</Text>
                    ) : (
                        <VStack gap={0} w="full" align="stretch">
                            {recentCandidates.map(({ candidate, recruitment }) => (
                                <CandidateRow
                                    key={candidate.id}
                                    candidate={candidate}
                                    recruitment={recruitment}
                                    t={t}
                                    tModels={tModels}
                                    onClick={() => navigate(`/models/${resolveAgentId()}/recruitments/${recruitment.id}/candidates/${candidate.id}`)}
                                />
                            ))}
                        </VStack>
                    )}
                </Box>

                {/* Secondary: compact recruitments, activity and agents */}
                <VStack flex="2" minW={{ base: "full", lg: "260px" }} gap={4} align="stretch">
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                        <HStack justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                            <Text fontWeight={700} color="gray.900" fontSize="sm">{t("recruitmentDashboard.activeRecruitmentsTitle")}</Text>
                            <Text as="button" color="purple.600" fontSize="xs" fontWeight={600} onClick={() => navigate("/recruitment/all")} cursor="pointer">
                                {t("recruitmentDashboard.viewAll")}
                            </Text>
                        </HStack>
                        {displayedRecruitments.length === 0 ? (
                            <VStack align="flex-start" gap={2} p={4}>
                                <Text color="gray.500" fontSize="sm">{t("recruitmentDashboard.emptyRecruitments")}</Text>
                                <Button variant="outlined" size="sm" onClick={() => navigate("/models")}>
                                    {t("recruitmentDashboard.emptyRecruitmentsCta")}
                                </Button>
                            </VStack>
                        ) : (
                            <VStack gap={0} w="full" align="stretch">
                                {displayedRecruitments.map((recruitment) => (
                                    <RecruitmentRow
                                        key={recruitment.id}
                                        recruitment={recruitment}
                                        t={t}
                                        onClick={() => navigate(`/models/${resolveAgentId()}/recruitments/${recruitment.id}/candidates`)}
                                    />
                                ))}
                            </VStack>
                        )}
                    </Box>

                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                        <Text fontWeight={700} color="gray.900" fontSize="sm" mb={3}>{t("recruitmentDashboard.activityTitle")}</Text>
                        {activityFeed.length === 0 ? (
                            <Text color="gray.500" fontSize="sm">{t("recruitmentDashboard.emptyActivity")}</Text>
                        ) : (
                            <VStack align="stretch" gap={3}>
                                {activityFeed.map((item) => (
                                    <ActivityRow
                                        key={item.id}
                                        item={item}
                                        onClick={() => navigate(`/models/${resolveAgentId()}/recruitments/${item.recruitment.id}/candidates/${item.candidateId}`)}
                                    />
                                ))}
                            </VStack>
                        )}
                    </Box>

                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                        <Text fontWeight={700} color="gray.900" fontSize="sm" mb={3}>{t("recruitmentDashboard.agentsTitle")}</Text>
                        <HStack flexWrap="wrap" gap={2}>
                            {recruiterAgents.map((agent) => (
                                <Text
                                    key={agent.id}
                                    as="button"
                                    onClick={() => navigate(`/models/${agent.id}`)}
                                    bg="gray.50"
                                    _hover={{ bg: "gray.100" }}
                                    color="gray.700"
                                    fontSize="xs"
                                    fontWeight={500}
                                    px={3}
                                    py={1.5}
                                    borderRadius="999px"
                                    cursor="pointer"
                                >
                                    {agent.name}
                                </Text>
                            ))}
                        </HStack>
                    </Box>
                </VStack>
            </HStack>

            <NewRecruitmentModal
                isOpen={isNewRecruitmentModalOpen}
                onClose={() => setIsNewRecruitmentModalOpen(false)}
            />
        </VStack>
    );
};

export default RecruitmentDashboardPage;
